import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Search, Users } from "lucide-react";
import { Button } from "../../components/Button";
import { Card, CardContent, CardHeader } from "../../components/Card";
import { MembersModal } from "../../components/MembersModal";
import SuccessPopup from "../../components/SuccessPopup";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client";
import { ThreadListView } from "../Conversations/ThreadListView";
import { ThreadInterface } from "../Conversations/ThreadInterface";
import { CreateThreadModal } from "../Conversations/CreateThreadModal";
import { SearchMessagesModal } from "../Conversations/SearchMessagesModal";
import {
  INTERNAL_THREADS_ENDPOINT,
  buildInternalThreadUrl,
  decodeUrlSafeBase64,
} from "../Conversations/threadUrlUtils";

const PANELS_HEIGHT = "calc(100vh - 165px)";

export function InternalCommunication() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { threadId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser, interceptorReady } = useAuth();
  const { setPageContext, conversationsRefreshKey } = useOutletContext();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: "", isError: false });

  const showNotification = useCallback(({ message = "", isError = false } = {}) => {
    if (message) setNotification({ open: true, message, isError });
  }, []);

  const messageThreadId = decodeUrlSafeBase64(searchParams.get("mt"));
  const messageThreadTitle = searchParams.get("mtt") || "";

  const pageParam = parseInt(searchParams.get("page") ?? "", 10);
  const initialPage = !Number.isNaN(pageParam) && pageParam > 0 ? pageParam : 1;
  const highlightMessageId = searchParams.get("highlightMessage");
  const positionParam = parseInt(searchParams.get("messagePosition") ?? "", 10);
  const highlightMessagePosition = Number.isNaN(positionParam) ? null : positionParam;

  const { data: threadsData, isLoading: threadsLoading } = getData(
    INTERNAL_THREADS_ENDPOINT,
    interceptorReady,
    conversationsRefreshKey
  );
  const threads = useMemo(() => (Array.isArray(threadsData) ? threadsData : []), [threadsData]);

  const { data: selectedThread } = getData(
    threadId ? `communication/threads/${threadId}/` : null,
    interceptorReady && !!threadId,
    conversationsRefreshKey
  );

  useEffect(() => {
    setPageContext({
      title: "Canal interno",
      subtitle: "Conversaciones del equipo de la agencia",
      showTitle: true,
      rightContent: (
        <Button
          variant="dark"
          size="default"
          onClick={() => setShowSearchModal(true)}
          sx={{ gap: "8px" }}
        >
          <Search size={16} />
          Buscar mensajes
        </Button>
      ),
    });
  }, [setPageContext]);

  const handleSearchResultSelect = useCallback(
    async ({ result, page }) => {
      const targetThreadId = result?.thread_id || threadId;
      if (!targetThreadId) return;

      // Una respuesta se resalta a través de su mensaje padre
      let highlightId = result.id;
      try {
        const { data } = await apiClient.get(`communication/messages/${result.id}/`);
        const parentId = typeof data?.parent === "object" ? data.parent?.id : data?.parent;
        if (parentId) highlightId = parentId;
      } catch (error) {
        console.warn("No se pudo resolver el mensaje padre del resultado", error);
      }

      const params = new URLSearchParams();
      const targetPage = Number(page) || 1;
      if (targetPage > 1) params.set("page", String(targetPage));
      params.set("highlightMessage", String(highlightId));
      if (result.message_position != null) {
        params.set("messagePosition", String(result.message_position));
      }

      navigate(`${buildInternalThreadUrl(targetThreadId)}?${params.toString()}`);
    },
    [navigate, threadId]
  );

  const handleHighlightConsumed = useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete("highlightMessage");
        params.delete("messagePosition");
        return params;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Toast enviado al navegar (p. ej. tras mover un mensaje a un hilo nuevo)
  useEffect(() => {
    const toast = location.state?.toast;
    if (!toast?.message) return;
    showNotification(toast);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.key, location.pathname, location.search, location.state, navigate, showNotification]);

  // Sin hilo en la URL abre el más reciente
  useEffect(() => {
    if (threadId) return;
    const firstThread = threads.find((thread) => !thread.is_message_thread);
    if (firstThread) navigate(buildInternalThreadUrl(firstThread.id), { replace: true });
  }, [threadId, threads, navigate]);

  useEffect(() => {
    const ids = selectedThread?.unopened_notification_ids;
    if (!ids?.length) return;
    apiClient.post("dashboard/notifications/mark-opened/", { ids }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThread?.id, selectedThread?.unopened_notification_ids?.join(",")]);

  const handleThreadSelect = (_clientId, targetThreadId, options = {}) => {
    navigate(buildInternalThreadUrl(targetThreadId, options));
  };

  const handleThreadCreated = (thread) => {
    queryClient.invalidateQueries({ queryKey: ["getData", INTERNAL_THREADS_ENDPOINT] });
    showNotification({ message: "Hilo creado correctamente" });
    navigate(buildInternalThreadUrl(thread.id));
  };

  const handleTitleUpdate = async ({ threadId: targetId, title, isMessageThread = false }) => {
    const trimmedTitle = (title || "").trim();
    if (trimmedTitle.length < 3) {
      throw new Error("El título debe tener al menos 3 caracteres.");
    }
    const response = isMessageThread
      ? await apiClient.patch(`communication/messages/${targetId}/`, { is_thread_title: trimmedTitle })
      : await apiClient.patch(`communication/threads/${targetId}/`, { title: trimmedTitle });
    queryClient.invalidateQueries({ queryKey: ["getData", INTERNAL_THREADS_ENDPOINT] });
    showNotification({ message: "Título actualizado correctamente" });
    return response.data;
  };

  const participants = selectedThread?.participants || [];
  const threadData = selectedThread
    ? {
      id: null,
      name: selectedThread.title,
      agency: selectedThread.agency,
      participants,
    }
    : null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.5fr 3.5fr" },
        gap: "24px",
        height: { md: PANELS_HEIGHT },
      }}
    >
      <Card sx={{ overflowY: "auto", padding: 0, height: "100%", maxHeight: { md: PANELS_HEIGHT } }}>
        <CardHeader sx={{ paddingBottom: "12px", borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Hilos
            </Typography>
            <Tooltip title="Ver participantes">
              <span>
                <IconButton
                  size="small"
                  disabled={!selectedThread}
                  onClick={() => setShowMembersModal(true)}
                  sx={{ color: theme.palette.text.secondary, "&:hover": { color: theme.palette.primary.main } }}
                >
                  <Users size={18} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </CardHeader>
        <CardContent sx={{ padding: 0 }}>
          <ThreadListView
            threads={threads}
            selectedThreadId={threadId}
            selectedMessageThreadId={messageThreadId}
            onThreadSelect={handleThreadSelect}
            onCreateThread={() => setShowCreateModal(true)}
            isLoading={threadsLoading}
            canCreateThreads
            onUpdateThreadTitle={handleTitleUpdate}
          />
        </CardContent>
      </Card>

      <ThreadInterface
        internal
        mainThreadId={threadId || null}
        messageThreadId={messageThreadId}
        messageThreadTitle={messageThreadTitle}
        currentUser={currentUser}
        clientData={threadData}
        participants={participants}
        conversationsRefreshKey={conversationsRefreshKey}
        initialPage={initialPage}
        highlightMessageId={highlightMessageId}
        highlightMessagePosition={highlightMessagePosition}
        onHighlightConsumed={handleHighlightConsumed}
        onShowNotification={showNotification}
        onMessageSent={() =>
          queryClient.invalidateQueries({ queryKey: ["getData", INTERNAL_THREADS_ENDPOINT] })
        }
        height={PANELS_HEIGHT}
      />

      <CreateThreadModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        client={null}
        onSuccess={handleThreadCreated}
      />

      <MembersModal
        open={showMembersModal}
        onClose={() => setShowMembersModal(false)}
        participants={participants}
        clientName={selectedThread?.title || ""}
        agencyName={selectedThread?.agency?.name || ""}
      />

      <SearchMessagesModal
        internal
        open={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        currentUser={currentUser}
        onResultSelect={handleSearchResultSelect}
      />

      <SuccessPopup
        message={notification.message}
        isOpen={notification.open}
        onClose={() => setNotification({ open: false, message: "", isError: false })}
        error={notification.isError}
      />
    </Box>
  );
}
